import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Button from '@mui/material/Button';
import {
  Grid,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
  Input,
  Alert,
  Checkbox,
  CircularProgress,
  Backdrop,
} from '@mui/material';
import ClassicModal from '../../components/common/ClassicModal';
import { useStyles } from '../../theme/VelocityHubMaterial';
import MaterialMultiSelect from '../../components/materialMultiSelect/MaterialMultiSelect';
import {
  UsersScheduledQueriesFrequencyTypes,
  UsersScheduledQueriesFormatTypes,
  UsersScheduledQueriesFormatSeparatorTypes,
  UsersScheduledQueriesDeliveryTypes,
  UsersScheduledQueriesCompressionTypes,
  AlertSeverity,
} from '../../utils/constants/enums';
import { useAuthentication } from '../../contexts/Authentication';
import { getPersonaDisplay } from '../../services/persona.api';

function BulkScheduleModal({ show, onHideModal, onConfirmAction, reportCount, initializeEmails, isLoading }) {
  const { classes } = useStyles();
  const { userInfo } = useAuthentication();

  const [scheduleConfig, setScheduleConfig] = useState({
    userScheduledQueryFrequencyTypeId: UsersScheduledQueriesFrequencyTypes.Monthly,
    userScheduledQueryFormatTypeId: UsersScheduledQueriesFormatTypes.PDF,
    userScheduledQueryFormatSeparatorTypeId: UsersScheduledQueriesFormatSeparatorTypes.Tab,
    userScheduledQueryDeliveryTypeId: UsersScheduledQueriesDeliveryTypes.Email,
    userScheduledQueryCompressionTypeId: UsersScheduledQueriesCompressionTypes.Zip,
    userScheduledQueryIsSingleXlsActive: false,
  });

  const [scheduledQueryEmails, setScheduledQueryEmails] = useState({});
  const [isPDFSelected, setIsPDFSelected] = useState(true);
  const [isExcelSelected, setIsExcelSelected] = useState(false);
  const [isDelimitedTextSelected, setIsDelimitedTextSelected] = useState(false);
  const [availableEmails, setAvailableEmails] = useState(initializeEmails || []);
  const [validationMessage, setValidationMessage] = useState('');

  const handleEmailChange = (data) => {
    setValidationMessage('');
    if (data && data.emailAddress) {
      setScheduledQueryEmails(data.emailAddress);
    }
  };

  // Initialize default email when modal opens
  useEffect(() => {
    if (show && initializeEmails && initializeEmails.length > 0) {
      setAvailableEmails(initializeEmails);
      
      // Get persona information if available
      getPersonaDisplay()
        .then((personaDetails) => {
          // Determine which email to use (persona or logged-in user)
          let userToCompareEmail = userInfo.email;
          const personaStatus = localStorage.getItem('personaStatus');
          if (personaStatus === 'true' && personaDetails?.personaEmail) {
            userToCompareEmail = personaDetails.personaEmail;
          }

          // Find and set the default email
          const filteredResult = initializeEmails.find(
            (element) => element.emailAddress === userToCompareEmail
          );
          
          if (filteredResult) {
            const mail = filteredResult.emailAddress;
            const formattedResult = {};
            formattedResult[mail] = filteredResult;
            const nestedObject = { emailAddress: formattedResult };
            // Initialize the emails by calling handleEmailChange
            handleEmailChange(nestedObject);
          }
        })
        .catch(() => {
          // If persona fetch fails, just use the logged-in user email
          const filteredResult = initializeEmails.find(
            (element) => element.emailAddress === userInfo.email
          );
          
          if (filteredResult) {
            const mail = filteredResult.emailAddress;
            const formattedResult = {};
            formattedResult[mail] = filteredResult;
            const nestedObject = { emailAddress: formattedResult };
            // Initialize the emails by calling handleEmailChange
            handleEmailChange(nestedObject);
          }
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, initializeEmails, userInfo]);

  const handleFrequencyChange = (event) => {
    setScheduleConfig({
      ...scheduleConfig,
      userScheduledQueryFrequencyTypeId: parseInt(event.target.value, 10),
    });
  };

  const handleCompressionChange = (event) => {
    setScheduleConfig({
      ...scheduleConfig,
      userScheduledQueryCompressionTypeId: parseInt(event.target.value, 10),
    });
  };

  const handleDeliveryChange = (event) => {
    setScheduleConfig({
      ...scheduleConfig,
      userScheduledQueryDeliveryTypeId: parseInt(event.target.value, 10),
    });
  };

  const handleSingleXlsChange = (event) => {
    setScheduleConfig({
      ...scheduleConfig,
      userScheduledQueryIsSingleXlsActive: event.target.checked,
    });
  };

  const handleFormatChange = (value) => {
    let formatTypeId = UsersScheduledQueriesFormatTypes.PDF;
    
    if (value.pdf && value.excel && value.delimitedText) {
      formatTypeId = UsersScheduledQueriesFormatTypes.PDF_AND_EXCEL_AND_DELIMITED_TEXT;
    } else if (value.pdf && value.excel) {
      formatTypeId = UsersScheduledQueriesFormatTypes.PDF_AND_EXCEL;
    } else if (value.pdf && value.delimitedText) {
      formatTypeId = UsersScheduledQueriesFormatTypes.PDF_AND_DELIMITED_TEXT;
    } else if (value.excel && value.delimitedText) {
      formatTypeId = UsersScheduledQueriesFormatTypes.EXCEL_AND_DELIMITED_TEXT;
    } else if (value.excel) {
      formatTypeId = UsersScheduledQueriesFormatTypes.EXCEL;
    } else if (value.delimitedText) {
      formatTypeId = UsersScheduledQueriesFormatTypes.DELIMITED_TEXT;
    }

    setScheduleConfig({
      ...scheduleConfig,
      userScheduledQueryFormatTypeId: formatTypeId,
    });
    setIsPDFSelected(value.pdf);
    setIsExcelSelected(value.excel);
    setIsDelimitedTextSelected(value.delimitedText);
  };

  const handleConfirm = (runNow = false) => {
    // Validation
    if (scheduleConfig.userScheduledQueryFormatTypeId === 0) {
      setValidationMessage('Format Type is required.');
      return;
    }

    if (!scheduledQueryEmails || Object.keys(scheduledQueryEmails).length === 0) {
      setValidationMessage('At least one email recipient is required.');
      return;
    }

    // Pass the configuration to the parent with runNow flag
    onConfirmAction(scheduleConfig, Object.values(scheduledQueryEmails), runNow);
  };

  const handleClose = () => {
    // Reset state when closing
    setScheduleConfig({
      userScheduledQueryFrequencyTypeId: UsersScheduledQueriesFrequencyTypes.Monthly,
      userScheduledQueryFormatTypeId: UsersScheduledQueriesFormatTypes.PDF,
      userScheduledQueryFormatSeparatorTypeId: UsersScheduledQueriesFormatSeparatorTypes.Tab,
      userScheduledQueryDeliveryTypeId: UsersScheduledQueriesDeliveryTypes.Email,
      userScheduledQueryCompressionTypeId: UsersScheduledQueriesCompressionTypes.Zip,
      userScheduledQueryIsSingleXlsActive: false,
    });
    setScheduledQueryEmails({});
    setIsPDFSelected(true);
    setIsExcelSelected(false);
    setIsDelimitedTextSelected(false);
    setValidationMessage('');
    onHideModal();
  };

  const bulkScheduleModalBody = (
    <div className={classes.root} style={{ padding: '20px', maxHeight: '70vh', overflowY: 'auto', position: 'relative' }}>
      {/* Loading Backdrop */}
      {isLoading && (
        <Backdrop
          open={isLoading}
          style={{
            position: 'absolute',
            zIndex: 9999,
            color: '#fff',
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            borderRadius: '4px',
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px' }}>
            <CircularProgress color="inherit" size={60} />
            <Typography variant="h6" style={{ color: '#fff' }}>
              Scheduling {reportCount} report(s)...
            </Typography>
          </div>
        </Backdrop>
      )}

      {validationMessage && (
        <Alert severity="error" style={{ marginBottom: '10px' }}>
          {validationMessage}
        </Alert>
      )}

      <Grid container spacing={2}>
        {/* Configuration Message */}
        <Grid item xs={12}>
          <Typography variant="body1" gutterBottom style={{ marginBottom: '10px' }}>
            Configure schedule settings for <strong>{reportCount}</strong> selected report(s). 
            Reports without a schedule will be created, and reports with existing schedules will be updated. 
            All reports will use the same settings below:
          </Typography>
        </Grid>

        {/* Frequency Type */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" gutterBottom>
            Frequency Type <span style={{ color: 'red' }}>*</span>
          </Typography>
          <FormControl component="fieldset">
            <RadioGroup
              value={scheduleConfig.userScheduledQueryFrequencyTypeId}
              onChange={handleFrequencyChange}
            >
              <FormControlLabel
                value={UsersScheduledQueriesFrequencyTypes.Monthly}
                control={<Radio />}
                label="Monthly"
              />
              <FormControlLabel
                value={UsersScheduledQueriesFrequencyTypes.CalendarQuarter}
                control={<Radio />}
                label="Calendar Quarter"
              />
              <FormControlLabel
                value={UsersScheduledQueriesFrequencyTypes.CalendarYear}
                control={<Radio />}
                label="Calendar Year"
              />
            </RadioGroup>
          </FormControl>
        </Grid>

        {/* Format Type */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" gutterBottom>
            Format Type <span style={{ color: 'red' }}>*</span>
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={isPDFSelected}
                onChange={(e) =>
                  handleFormatChange({
                    pdf: e.target.checked,
                    excel: isExcelSelected,
                    delimitedText: isDelimitedTextSelected,
                  })
                }
              />
            }
            label="PDF"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={isExcelSelected}
                onChange={(e) =>
                  handleFormatChange({
                    pdf: isPDFSelected,
                    excel: e.target.checked,
                    delimitedText: isDelimitedTextSelected,
                  })
                }
              />
            }
            label="Excel"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={isDelimitedTextSelected}
                onChange={(e) =>
                  handleFormatChange({
                    pdf: isPDFSelected,
                    excel: isExcelSelected,
                    delimitedText: e.target.checked,
                  })
                }
              />
            }
            label="CSV"
          />
        </Grid>

        {/* Single XLS Option (only show if Excel is selected) */}
        {isExcelSelected && (
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={scheduleConfig.userScheduledQueryIsSingleXlsActive}
                  onChange={handleSingleXlsChange}
                />
              }
              label="Combine all reports into a single Excel file (sheets)"
            />
          </Grid>
        )}

        {/* Delivery Type */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" gutterBottom>
            Delivery Type <span style={{ color: 'red' }}>*</span>
          </Typography>
          <FormControl component="fieldset">
            <RadioGroup
              value={scheduleConfig.userScheduledQueryDeliveryTypeId}
              onChange={handleDeliveryChange}
            >
              <FormControlLabel
                value={UsersScheduledQueriesDeliveryTypes.Email}
                control={<Radio />}
                label="Email"
              />
            </RadioGroup>
          </FormControl>
        </Grid>

        {/* Compression Type */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" gutterBottom>
            Compression Type <span style={{ color: 'red' }}>*</span>
          </Typography>
          <FormControl component="fieldset">
            <RadioGroup
              value={scheduleConfig.userScheduledQueryCompressionTypeId}
              onChange={handleCompressionChange}
            >
              <FormControlLabel
                value={UsersScheduledQueriesCompressionTypes.Zip}
                control={<Radio />}
                label="Zip"
              />
              <FormControlLabel
                value={UsersScheduledQueriesCompressionTypes.Gzip}
                control={<Radio />}
                label="Gzip"
              />
              <FormControlLabel
                value={UsersScheduledQueriesCompressionTypes.None}
                control={<Radio />}
                label="None"
              />
            </RadioGroup>
          </FormControl>
        </Grid>

        {/* Email Recipients */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" gutterBottom>
            Email Recipients <span style={{ color: 'red' }}>*</span>
          </Typography>
          <FormControl sx={{ minWidth: 300 }}>
            <MaterialMultiSelect
              testid="Emails"
              options={availableEmails}
              label="Emails"
              objectRenderField="fullNmAndEmailAddress"
              onChange={handleEmailChange}
              queryDataType="emailAddress"
              selectAttribute="emailAddress"
              value={scheduledQueryEmails}
              defaultAll={false}
              hideAllCheckBox
              disabled={availableEmails.length === 0}
              dataCy="bulk-schedule-email-input"
            />
          </FormControl>
        </Grid>
      </Grid>
    </div>
  );

  const bulkScheduleModalFooter = (
    <div className={classes.root} style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
      <Button
        variant="contained"
        color="primary"
        onClick={() => handleConfirm(true)}
        disabled={
          isLoading ||
          Object.keys(scheduledQueryEmails).length === 0 ||
          scheduleConfig.userScheduledQueryFormatTypeId === 0
        }
        data-cy="bulk-schedule-run-now-button"
        startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
      >
        Schedule & Run Now
      </Button>
      <Button
        variant="contained"
        color="primary"
        onClick={() => handleConfirm(false)}
        disabled={
          isLoading ||
          Object.keys(scheduledQueryEmails).length === 0 ||
          scheduleConfig.userScheduledQueryFormatTypeId === 0
        }
        data-cy="bulk-schedule-button-confirm"
        startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
      >
        Schedule All
      </Button>
      <Button
        variant="contained"
        onClick={handleClose}
        disabled={isLoading}
        data-cy="bulk-schedule-button-cancel"
      >
        Cancel
      </Button>
    </div>
  );

  return (
    <div>
      <ClassicModal
        show={show}
        className={classes.root}
        onHide={handleClose}
        title="Schedule Multiple Reports"
        body={bulkScheduleModalBody}
        footer={bulkScheduleModalFooter}
        size="lg"
      />
    </div>
  );
}

BulkScheduleModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHideModal: PropTypes.func.isRequired,
  onConfirmAction: PropTypes.func.isRequired,
  reportCount: PropTypes.number.isRequired,
  initializeEmails: PropTypes.array,
  isLoading: PropTypes.bool,
};

BulkScheduleModal.defaultProps = {
  initializeEmails: [],
  isLoading: false,
};

export default BulkScheduleModal;

